import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useStoreDispatch, useStoreState } from 'easy-peasy';
import { api } from '~/api';
import dayjs from 'dayjs';
import { useBodyClassReverse } from '~/hooks';
import { Checkbox, Button } from 'Components/nui';
import { FormattedMessage, useIntl } from 'react-intl';
import { ROUTES } from '~/Routes';
import { toast } from 'react-toastify';

// Static FormattedMessage components for translation extraction
const POLICY_MESSAGES = {
  terms: {
    header: (labelComponent) => (
      <FormattedMessage
        id="privacy-dialog-read-accept-terms-message"
        defaultMessage="Please read and accept the {termsOfUse} prior to proceeding to the platform."
        values={{ termsOfUse: labelComponent }}
      />
    ),
    label: (
      <FormattedMessage id="terms-of-use-label" defaultMessage="terms of use" />
    ),
    checkbox: (linkComponent) => (
      <FormattedMessage
        id="privacy-dialog-terms-checkbox"
        defaultMessage="I have read and understand the {termsLink}"
        values={{ termsLink: linkComponent }}
      />
    ),
    linkDefault: 'terms of use',
  },
  privacy: {
    header: (labelComponent) => (
      <FormattedMessage
        id="privacy-dialog-read-accept-privacy-message"
        defaultMessage="Please read and accept the {privacyStatement} prior to proceeding to the platform."
        values={{ privacyStatement: labelComponent }}
      />
    ),
    label: (
      <FormattedMessage
        id="privacy-statement-label"
        defaultMessage="privacy statement"
      />
    ),
    checkbox: (linkComponent) => (
      <FormattedMessage
        id="privacy-dialog-privacy-checkbox"
        defaultMessage="I have read and understand the {privacyLink}"
        values={{ privacyLink: linkComponent }}
      />
    ),
    linkDefault: 'privacy statement',
  },
  'nui-terms': {
    header: (labelComponent) => (
      <FormattedMessage
        id="privacy-dialog-nui-terms-message"
        defaultMessage="Please read and accept the {nuiTerms} prior to proceeding to the platform."
        values={{ nuiTerms: labelComponent }}
      />
    ),
    label: (
      <FormattedMessage
        id="nui-terms-of-use-label"
        defaultMessage="Nui terms of use"
      />
    ),
    checkbox: (linkComponent) => (
      <FormattedMessage
        id="privacy-dialog-nui-terms-checkbox"
        defaultMessage="I have read and understand the {nuiTermsLink}"
        values={{ nuiTermsLink: linkComponent }}
      />
    ),
    linkDefault: 'Nui terms of use',
  },
};

const UPDATED_MESSAGE = (date) => (
  <FormattedMessage
    id="privacy-dialog-updated-on"
    defaultMessage="(updated on {date})"
    values={{ date }}
  />
);

export default () => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const dispatch = useStoreDispatch();
  const solution = useStoreState((state) => state.auth.solution);
  const policesToAccept = solution.policies.filter((p) => p.accepted === false);

  // Track acceptance status for each policy by index
  const [acceptedPolicies, setAcceptedPolicies] = useState({});

  const [loading, setLoading] = useState(false);

  // Group and order policies: terms -> privacy -> nui-terms
  const policyOrder = ['terms', 'privacy', 'nui-terms'];
  const groupedPolicies = policyOrder
    .map((name) => ({
      name,
      policies: policesToAccept.filter((p) => p.name === name),
    }))
    .filter((group) => group.policies.length > 0);

  const togglePolicy = (index) => {
    setAcceptedPolicies((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const allPoliciesAccepted = policesToAccept.every(
    (_, index) => acceptedPolicies[index]
  );

  async function accept() {
    setLoading(true);
    try {
      await api.acceptPolicy(solution.id);
      await dispatch.auth.checkAuth({ redirect: false });
      history.push(ROUTES.home);
    } catch {
      toast.error(
        formatMessage({
          id: 'privacy-dialog-accept-error',
          defaultMessage: 'Failed to accept terms. Please try again.',
        })
      );
      setLoading(false);
    }
  }

  useBodyClassReverse();

  return (
    <section className="main-content privy-content" role="main">
      <div>
        <div className="privy">
          <h1>
            <FormattedMessage
              id="privacy-dialog-title"
              defaultMessage="Terms and privacy statement"
            />
          </h1>
        </div>
      </div>

      {groupedPolicies.map((group, groupIndex) => {
        const messages = POLICY_MESSAGES[group.name];

        return (
          <div key={group.name} className={groupIndex > 0 ? 'mt-20' : ''}>
            <div className="privy">
              <p>
                {messages.header(
                  <strong className="all-black">{messages.label}</strong>
                )}
              </p>
              {group.policies.map((policy) => {
                const policyIndex = policesToAccept.indexOf(policy);
                const solutionName = policy.solution || solution?.name;
                const linkText = solutionName
                  ? `${solutionName} ${messages.linkDefault}`
                  : messages.linkDefault;

                return (
                  <Checkbox
                    key={policyIndex}
                    checked={!!acceptedPolicies[policyIndex]}
                    onChange={() => togglePolicy(policyIndex)}
                    title=""
                    disabled={loading}
                  >
                    {messages.checkbox(
                      <a
                        href={policy.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {linkText}
                      </a>
                    )}
                    {policy.updated && (
                      <em>
                        {' '}
                        {UPDATED_MESSAGE(dayjs(policy.updated).format('ll'))}
                      </em>
                    )}
                  </Checkbox>
                );
              })}
            </div>
          </div>
        );
      })}

      <div>
        <div className="privy">
          <hr />
          <div className="button-set pt-20">
            <Button
              type="primary"
              disabled={!allPoliciesAccepted}
              onClick={accept}
              loading={loading}
            >
              <FormattedMessage
                id="privacy-dialog-accept-button"
                defaultMessage="Accept"
              />
            </Button>
            <Button type="simple" onClick={() => dispatch.auth.signOut()}>
              <FormattedMessage
                id="privacy-dialog-sign-out-button"
                defaultMessage="Sign Out"
              />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
